import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/SurveyFeedbackTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { surveysAPI, fileAPI } from "../../api/index"; // adjust path to match your project
import {
  FaClipboardList,
  FaCheckCircle,
  FaRegClock,
  FaStar,
  FaSearch,
  FaFilter,
  FaMapMarkerAlt,
  FaSyncAlt,
  FaEdit,
  FaTrashAlt,
  FaRoad,
  FaTint,
  FaExclamationTriangle,
  FaThumbsUp,
  FaSmile,
  FaMeh,
  FaFrown,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaDownload,
} from "react-icons/fa";

// ---------- Excel template settings ----------
// Headings follow the Add Feedback form fields (same order as the form).
// CONFIRM these with your backend if you add a bulk upload for surveys.
const TEMPLATE_HEADERS = [
  "Respondent Name",
  "Mobile Number",
  "Voter ID",
  "Category",
  "Feedback Type",
  "Satisfaction Rating",
  "Village",
  "Mandal",
  "Date",
  "Status",
  "Feedback Details",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 28 }, // Respondent Name
  { wch: 16 }, // Mobile Number
  { wch: 16 }, // Voter ID
  { wch: 22 }, // Category
  { wch: 18 }, // Feedback Type
  { wch: 22 }, // Satisfaction Rating
  { wch: 22 }, // Village
  { wch: 20 }, // Mandal
  { wch: 14 }, // Date
  { wch: 14 }, // Status
  { wch: 55 }, // Feedback Details
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Respondent Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Mobile Number", "Yes", "Exactly 10 digits, must start with 6, 7, 8 or 9. Format the cell as Text in Excel"],
  ["Voter ID", "No", "Optional. If filled: 3 capital letters + 7 digits (e.g. ABC1234567)"],
  ["Category", "Yes", "Road Development, Drinking Water, Electricity, Health Camp, Education, Sanitation, General"],
  ["Feedback Type", "Yes", "Complaint, Suggestion, Appreciation"],
  ["Satisfaction Rating", "Yes", "Satisfied, Neutral, Dissatisfied"],
  ["Village", "Yes", "Alphabets and spaces only (2-100 characters)"],
  ["Mandal", "Yes", "Kandukur, Gudluru, Ulavapadu"],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", "Reviewed, Pending"],
  ["Feedback Details", "Yes", "10-1000 characters"],
  ["Photo", "-", "Photos cannot be added in Excel. Upload them through the Add Feedback form"],
];

const CATEGORY_ICON_MAP = {
  "Road Development": <FaRoad />,
  "Drinking Water": <FaTint />,
  "Health Camp": <FaThumbsUp />,
  Electricity: <FaExclamationTriangle />,
  Education: <FaClipboardList />,
  Sanitation: <FaTint />,
  General: <FaClipboardList />,
};

const AVATAR_COLORS = ["sf-avatar-blue", "sf-avatar-orange", "sf-avatar-green"];

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

// Backend stores satisfaction_rating as 1-5; UI shows 3 buckets.
const scoreToRatingLabel = (score) => {
  if (score == null) return "—";
  if (score >= 4) return "Satisfied";
  if (score <= 2) return "Dissatisfied";
  return "Neutral";
};
const RATING_TO_SCORE = { Satisfied: 5, Neutral: 3, Dissatisfied: 1 };

// Backend status is lowercase ("pending"/"reviewed"); UI is Capitalized.
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Backend gives "YYYY-MM-DD"; UI originally showed "DD-MM-YYYY".
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const PAGE_SIZE = 10;

const SurveyFeedbackTable = () => {
  const navigate = useNavigate();

  const [surveyData, setSurveyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- PHOTO PREVIEW (in-page popup) ----------
  const [previewPhoto, setPreviewPhoto] = useState(null); // holds the item
  const openPhotoPreview = (item) => setPreviewPhoto(item);
  const closePhotoPreview = () => setPreviewPhoto(null);

  const typeIcons = {
    Complaint: <FaExclamationTriangle />,
    Suggestion: <FaRegClock />,
    Appreciation: <FaThumbsUp />,
  };

  const ratingIcons = {
    Satisfied: <FaSmile />,
    Neutral: <FaMeh />,
    Dissatisfied: <FaFrown />,
  };

  const statusIcons = {
    Reviewed: <FaCheckCircle />,
    Pending: <FaRegClock />,
  };

  // ---------- FETCH ----------
  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await surveysAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setSurveyData(data);
    } catch (err) {
      console.error("Failed to load surveys:", err);
      setLoadError(err.response?.data?.detail || "Could not load surveys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  // Close photo preview with Escape key
  useEffect(() => {
    if (!previewPhoto) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePhotoPreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewPhoto]);

  /* ===================== DOWNLOAD EXCEL TEMPLATE (headings only) ===================== */

  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 24 }, { wch: 10 }, { wch: 90 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Survey_Feedback_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  /* ===================== SEARCH + FILTER LOGIC (client-side) ===================== */

  const filteredData = surveyData.filter((item) => {
    const name = item.respondent_name || "";
    const mobile = item.mobile_number || "";

    const matchesSearch =
      searchTerm.trim() === "" ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mobile.includes(searchTerm.trim());

    const matchesCategory =
      categoryFilter === "All Categories" || item.category === categoryFilter;

    // NOTE: backend only returns mandal_id (int), not mandal name, so this
    // filter can't match by name until the API exposes mandal_name on read.
    const matchesMandal =
      mandalFilter === "All Mandals" || String(item.mandal_id) === mandalFilter;

    const matchesStatus =
      statusFilter === "All Status" ||
      (item.status || "").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesCategory && matchesMandal && matchesStatus;
  });

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("All Categories");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const totalEntries = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const startIndex = (safePage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pageData = filteredData.slice(startIndex, endIndex);

  /* ===================== STATS (computed from loaded data) ===================== */

  const totalFeedback = surveyData.length;
  const reviewedCount = surveyData.filter(
    (s) => (s.status || "").toLowerCase() === "reviewed"
  ).length;
  const pendingCount = surveyData.filter(
    (s) => (s.status || "").toLowerCase() === "pending"
  ).length;
  const satisfiedCount = surveyData.filter(
    (s) => s.satisfaction_rating != null && s.satisfaction_rating >= 4
  ).length;
  const satisfactionRate =
    totalFeedback > 0 ? Math.round((satisfiedCount / totalFeedback) * 100) : 0;

  /* ===================== EDIT LOGIC ===================== */

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.respondent_name || "",
      mobile: item.mobile_number || "",
      voterId: item.epic_no || "",
      category: item.category || "",
      type: item.feedback_type || "",
      rating: scoreToRatingLabel(item.satisfaction_rating),
      village: "", // backend doesn't return village name, only village_id
      mandal: "", // same for mandal
      date: item.survey_date || "",
      status: capitalize(item.status),
      details: item.feedback_details || "",
      photo_url: item.photo_url || null,
    });
    setEditPhoto(null);
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
    setEditPhoto(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) setEditPhoto(file);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let photoUrl = editForm.photo_url;
      if (editPhoto) {
        const uploadRes = await fileAPI.upload(editPhoto, "surveys");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          photoUrl;
      }

      await surveysAPI.update(editItem.id, {
        respondent_name: editForm.name,
        mobile_number: editForm.mobile,
        epic_no: editForm.voterId || null,
        category: editForm.category,
        feedback_type: editForm.type,
        satisfaction_rating: RATING_TO_SCORE[editForm.rating] ?? null,
        village_name: editForm.village || undefined,
        mandal_name: editForm.mandal || undefined,
        survey_date: editForm.date,
        status: editForm.status.toLowerCase(),
        feedback_details: editForm.details,
        photo_url: photoUrl,
      });

      await fetchSurveys();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update survey:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  /* ===================== DELETE LOGIC ===================== */

  const openDeleteModal = (item) => setDeleteItem(item);
  const closeDeleteModal = () => setDeleteItem(null);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await surveysAPI.delete(deleteItem.id);
      await fetchSurveys();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete survey:", err);
      alert(err.response?.data?.detail || "Could not delete this feedback.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sf-container">
      {/* Header */}
      <div className="sf-header">
        <div>
          <h2 className="sf-title">Surveys &amp; Feedback</h2>
          <p className="sf-subtitle">
            Manage and track surveys and feedback submitted by citizens.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="sf-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="sf-add-plus">
              <FaDownload />
            </span>{" "}
            Download Template
          </button>

          <button
            className="sf-add-btn"
            onClick={() => navigate("/dashboard/SurveyFeedbackForm")}
          >
            <span className="sf-add-plus">+</span> Add New Feedback
          </button>
        </div>
      </div>

      {loadError && <div className="sf-no-data">{loadError}</div>}

      {/* Stat Cards */}
      <div className="sf-stats-row">
        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-blue">
            <FaClipboardList />
          </div>
          <div>
            <p className="sf-stat-label">Total Feedback</p>
            <h3 className="sf-stat-value">{totalFeedback}</h3>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-green">
            <FaCheckCircle />
          </div>
          <div>
            <p className="sf-stat-label">Reviewed</p>
            <h3 className="sf-stat-value">{reviewedCount}</h3>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-orange">
            <FaRegClock />
          </div>
          <div>
            <p className="sf-stat-label">Pending</p>
            <h3 className="sf-stat-value">{pendingCount}</h3>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-purple">
            <FaStar />
          </div>
          <div>
            <p className="sf-stat-label">Satisfaction Rate</p>
            <h3 className="sf-stat-value">{satisfactionRate}%</h3>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="sf-filter-section">
        <div className="sf-search-wrapper">
          <FaSearch className="sf-search-icon" />
          <input
            type="text"
            placeholder="Search by Name or Mobile..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="sf-select-wrapper">
          <FaFilter className="sf-select-icon" />
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Categories</option>
            <option>Road Development</option>
            <option>Drinking Water</option>
            <option>Electricity</option>
            <option>Health Camp</option>
            <option>Education</option>
            <option>Sanitation</option>
            <option>General</option>
          </select>
        </div>

        <div className="sf-select-wrapper">
          <FaMapMarkerAlt className="sf-select-icon" />
          <select
            value={mandalFilter}
            onChange={(e) => {
              setMandalFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Mandals</option>
            {/* Filtering by name isn't possible until the API returns mandal
                names — left as a placeholder select for now */}
          </select>
        </div>

        <div className="sf-select-wrapper">
          <FaFilter className="sf-select-icon" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Status</option>
            <option>Reviewed</option>
            <option>Pending</option>
          </select>
        </div>

        <button className="sf-clear-btn" onClick={handleClearFilters}>
          <FaSyncAlt /> Clear Filters
        </button>
      </div>

      {/* Table */}
      <div className="sf-table-wrapper">
        <table className="sf-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Category</th>
              <th>Type</th>
              <th>Rating</th>
              <th>Village/Mandal</th>
              <th>Photo</th>
              <th>Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="sf-no-data">Loading...</td>
              </tr>
            ) : pageData.length > 0 ? (
              pageData.map((item, index) => {
                const ratingLabel = scoreToRatingLabel(item.satisfaction_rating);
                const statusLabel = capitalize(item.status);

                return (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="sf-name-cell">
                        <span
                          className={`sf-avatar ${
                            AVATAR_COLORS[item.id % AVATAR_COLORS.length]
                          }`}
                        >
                          {getInitials(item.respondent_name)}
                        </span>
                        {item.respondent_name || "—"}
                      </div>
                    </td>
                    <td>{item.mobile_number || "—"}</td>
                    <td>
                      <span className="sf-category-pill">
                        {CATEGORY_ICON_MAP[item.category] || <FaClipboardList />}{" "}
                        {item.category || "—"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`sf-type sf-type-${(item.feedback_type || "").toLowerCase()}`}
                      >
                        {typeIcons[item.feedback_type]} {item.feedback_type || "—"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`sf-rating sf-rating-${ratingLabel
                          .toLowerCase()
                          .replace(" ", "-")}`}
                      >
                        {ratingIcons[ratingLabel]} {ratingLabel}
                      </span>
                    </td>
                    <td>
                      {/* Backend only returns village_id/mandal_id (numbers),
                          not names — showing IDs until the API adds names */}
                      {item.village_id != null || item.mandal_id != null
                        ? `V:${item.village_id ?? "—"} / M:${item.mandal_id ?? "—"}`
                        : "—"}
                    </td>
                    <td>
                      {item.photo_url ? (
                        <button
                          type="button"
                          className="sf-photo-thumb-btn"
                          onClick={() => openPhotoPreview(item)}
                          title="View photo"
                        >
                          <img
                            src={item.photo_url}
                            alt={`${item.respondent_name || "Feedback"} photo`}
                            className="sf-photo-thumb"
                          />
                        </button>
                      ) : (
                        <span className="sf-photo-none">No photo</span>
                      )}
                    </td>
                    <td>
                      <span className="sf-date-cell">
                        <FaCalendarAlt /> {formatDisplayDate(item.survey_date)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`sf-status ${
                          statusLabel === "Reviewed"
                            ? "sf-status-reviewed"
                            : "sf-status-pending"
                        }`}
                      >
                        {statusIcons[statusLabel]} {statusLabel}
                      </span>
                    </td>
                    <td className="sf-action-cell">
                      <button
                        className="sf-edit-btn"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="sf-delete-btn"
                        onClick={() => openDeleteModal(item)}
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="sf-no-data">
                  No matching feedback found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      <div className="sf-footer">
        <p className="sf-footer-text">
          {totalEntries > 0
            ? `Showing ${startIndex + 1} to ${Math.min(
                endIndex,
                totalEntries,
              )} of ${totalEntries} entries`
            : "Showing 0 entries"}
        </p>

        <div className="sf-pagination">
          <button
            className="sf-page-btn"
            onClick={() => goToPage(safePage - 1)}
            disabled={safePage === 1}
          >
            <FaChevronLeft />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`sf-page-btn ${
                safePage === page ? "sf-page-active" : ""
              }`}
              onClick={() => goToPage(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="sf-page-btn"
            onClick={() => goToPage(safePage + 1)}
            disabled={safePage === totalPages}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* ===================== PHOTO PREVIEW POPUP ===================== */}
      {previewPhoto && (
        <div className="sf-modal-overlay" onClick={closePhotoPreview}>
          <div
            className="sf-photo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sf-modal-header">
              <h3>{previewPhoto.respondent_name || "Feedback Photo"}</h3>
              <button className="sf-modal-close" onClick={closePhotoPreview}>
                <FaTimes />
              </button>
            </div>

            <div className="sf-photo-modal-body">
              {previewPhoto.photo_url ? (
                <img
                  src={previewPhoto.photo_url}
                  alt={`${previewPhoto.respondent_name || "Feedback"} photo`}
                  className="sf-photo-modal-img"
                />
              ) : (
                <div className="sf-no-data">No photo available.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===================== EDIT MODAL ===================== */}
      {editItem && editForm && (
        <div className="sf-modal-overlay" onClick={closeEditModal}>
          <div
            className="sf-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sf-modal-header">
              <h3>Edit Feedback</h3>
              <button className="sf-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="sf-modal-form" onSubmit={handleEditSave}>
              <div className="sf-form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  required
                />
              </div>

              <div className="sf-form-row">
                <label>Mobile</label>
                <input
                  type="text"
                  value={editForm.mobile}
                  onChange={(e) => handleEditChange("mobile", e.target.value)}
                  maxLength={10}
                  required
                />
              </div>

              <div className="sf-form-row">
                <label>Voter ID</label>
                <input
                  type="text"
                  value={editForm.voterId}
                  onChange={(e) => handleEditChange("voterId", e.target.value)}
                  maxLength={10}
                />
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) =>
                      handleEditChange("category", e.target.value)
                    }
                  >
                    <option>Road Development</option>
                    <option>Drinking Water</option>
                    <option>Electricity</option>
                    <option>Health Camp</option>
                    <option>Education</option>
                    <option>Sanitation</option>
                    <option>General</option>
                  </select>
                </div>

                <div className="sf-form-row">
                  <label>Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => handleEditChange("type", e.target.value)}
                  >
                    <option>Complaint</option>
                    <option>Suggestion</option>
                    <option>Appreciation</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Rating</label>
                  <select
                    value={editForm.rating}
                    onChange={(e) =>
                      handleEditChange("rating", e.target.value)
                    }
                  >
                    <option>Satisfied</option>
                    <option>Neutral</option>
                    <option>Dissatisfied</option>
                  </select>
                </div>

                <div className="sf-form-row">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      handleEditChange("status", e.target.value)
                    }
                  >
                    <option>Pending</option>
                    <option>Reviewed</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Village (leave blank to keep current)</label>
                  <input
                    type="text"
                    value={editForm.village}
                    placeholder="Only fill in to change"
                    onChange={(e) =>
                      handleEditChange("village", e.target.value)
                    }
                  />
                </div>

                <div className="sf-form-row">
                  <label>Mandal (leave blank to keep current)</label>
                  <select
                    value={editForm.mandal}
                    onChange={(e) =>
                      handleEditChange("mandal", e.target.value)
                    }
                  >
                    <option value="">Keep current</option>
                    <option>Kandukur</option>
                    <option>Gudluru</option>
                    <option>Ulavapadu</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => handleEditChange("date", e.target.value)}
                  required
                />
              </div>

              <div className="sf-form-row">
                <label>Replace Photo (optional)</label>
                <input type="file" accept="image/*" onChange={handleEditPhotoChange} />
              </div>

              <div className="sf-modal-actions">
                <button
                  type="button"
                  className="sf-modal-cancel-btn"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="sf-modal-save-btn" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== DELETE CONFIRMATION MODAL ===================== */}
      {deleteItem && (
        <div className="sf-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="sf-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sf-confirm-icon">
              <FaTrashAlt />
            </div>
            <h3 className="sf-confirm-title">Delete this feedback?</h3>
            <p className="sf-confirm-text">
              Are you sure you want to delete feedback from{" "}
              <strong>{deleteItem.respondent_name}</strong>? This action cannot be
              undone.
            </p>

            <div className="sf-confirm-actions">
              <button
                className="sf-confirm-cancel-btn"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="sf-confirm-delete-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyFeedbackTable;